"use client";

/**
 * Credit-estimate hook for the configure modal's review step.
 *
 * The backend `/enroll/estimate` is a POST, so Orval generated a *mutation*
 * hook. The review step wants query semantics (caching, `enabled` gating,
 * invalidation), so — per the frontend convention "for composite queries,
 * import the plain async functions from generated.ts and compose with a custom
 * useQuery" — we wrap the generated `estimateEnrollmentReveal` async fn in a
 * `useQuery`.
 *
 * Gating mirrors the backend contract: a sequence must be chosen and the cohort
 * must be 1–50 profiles (`EnrollmentRevealEstimateRequest` enforces 1 ≤ n ≤ 50).
 */

import { useQuery } from "@tanstack/react-query";

import { estimateEnrollmentReveal } from "@/lib/api/generated";

export interface UseEnrollmentEstimateArgs {
  jobId: string;
  sequenceId: string | null;
  jobCandidateIds: string[];
  enabled?: boolean;
}

export function useEnrollmentEstimate(args: UseEnrollmentEstimateArgs) {
  const { jobId, sequenceId, jobCandidateIds, enabled = true } = args;

  return useQuery({
    queryKey: ["enrollment-estimate", jobId, sequenceId, jobCandidateIds],
    queryFn: () =>
      estimateEnrollmentReveal(jobId, sequenceId as string, {
        job_candidate_ids: jobCandidateIds,
      }),
    enabled:
      enabled &&
      !!sequenceId &&
      jobCandidateIds.length > 0 &&
      jobCandidateIds.length <= 50,
  });
}
